
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, ChangeDetectorRef, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { VncLibraryService } from "@uxf/vnc-library";
import { Observable } from "rxjs";
import { take } from "rxjs/operators";
import { SetActiveProfile, SetRightSidebarStatus } from "../../actions/app";
import { SetEditTaskView, TasksRootState } from "../../task/store";
import { TaskRepository } from "../../task/repository/task.repository";
import { TaskService } from "../../task/task.service";
import { MessageTranslatorService } from "../../task/services";
import { TaskComposeComponent } from "../../task/components/task-compose/task-compose.component";

/** Wheel-menu action ids, in the order the menu renders them. */
const WHEEL_ACTIONS = {
  one: "audio-call",
  two: "chat",
  three: "video-call",
  four: "email",
  five: "ticket",
  six: "task",
  info: "profile"
};

/**
 * The cross-app actions the contact wheel menu offers: Talk audio/video/chat, mail,
 * VNCproject ticket, task creation and the profile sidebar. Extracted from AppComponent
 * (STR-05).
 */
@Injectable({
  providedIn: "root"
})
export class ContactActionsService {
  private store = inject<Store<TasksRootState>>(Store);
  private matDialog = inject(MatDialog);
  private taskService = inject(TaskService);
  private taskRepository = inject(TaskRepository);
  private messageTranslatorService = inject(MessageTranslatorService);
  private vncLibaryService = inject(VncLibraryService);

  /**
   * @param currentUser the logged-in user; several actions are refused against yourself,
   *        and the contact lookup differs for your own address.
   */
  public handleActionClick(v, currentUser, changeDetectionRef: ChangeDetectorRef): void {
    const jid = v.data;
    switch (WHEEL_ACTIONS[v.action]) {
      case "audio-call": this.startTalkAudioCall(jid, currentUser, changeDetectionRef); break;
      case "chat": this.startTalkChat(jid, currentUser); break;
      case "video-call": this.startTalkVideoCall(jid, currentUser); break;
      case "email": this.sendEmail(jid); break;
      case "ticket": this.createTicket(jid, currentUser); break;
      case "task": this.createTask(jid, currentUser, changeDetectionRef); break;
      case "profile": this.openProfile(jid); break;
      default: break;
    }
  }

  public startTalkAudioCall(email, currentUser, changeDetectionRef?: ChangeDetectorRef): void {
    if (this.isSelf(email, currentUser)) {
      this.showMessage("CAN_NOT_CALL_YOURSELF");
      changeDetectionRef?.markForCheck();
      return;
    }
    this.taskRepository.makeTalkAudioChatVideoOperation(email, "audio-call", "group-audio");
  }

  public startTalkVideoCall(email, currentUser): void {
    if (this.isSelf(email, currentUser)) {
      this.showMessage("CAN_NOT_CALL_YOURSELF");
      return;
    }
    this.taskRepository.makeTalkAudioChatVideoOperation(email, "video-call", "group-video");
  }

  public startTalkChat(email, currentUser): void {
    if (this.isSelf(email, currentUser)) {
      this.showMessage("CAN_NOT_CHAT_YOURSELF");
      return;
    }
    this.taskRepository.makeTalkAudioChatVideoOperation(email, "start-chat", "group-chat");
  }

  public sendEmail(email): void {
    console.log("[ContactActionsService][sendEmail]::", email);
    this.taskRepository.sendEmail(email);
  }

  public openProfile(email): void {
    this.store.dispatch(new SetActiveProfile(email));
    this.store.dispatch(new SetRightSidebarStatus(true));
  }

  /** Opens a VNCproject ticket for the contact, if they have access to the app. */
  public createTicket(email, currentUser): void {
    this.withContactApp(email, currentUser, "vncproject", contact => {
      this.taskRepository.createTicket(contact.username);
    });
  }

  /** Opens the compose dialog assigned to the contact, if they have access to VNCtask. */
  public createTask(email, currentUser, changeDetectionRef: ChangeDetectorRef): void {
    this.withContactApp(email, currentUser, "vnctask", contact => {
      const username = contact?.username || contact?.email.split("@")[0];
      if (username) {
        this.openCreateTaskDialog(username, changeDetectionRef);
      }
    });
  }

  public openCreateTaskDialog(username, changeDetectionRef: ChangeDetectorRef): void {
    this.store.dispatch(new SetEditTaskView(false));
    this.matDialog.open(TaskComposeComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "task-compose-dialog-new",
      data: { assignee: username }
    });
    changeDetectionRef.markForCheck();
  }

  /**
   * Looks the contact up and runs the action only if they have the named app. The lookup
   * endpoint differs for your own address, which is the only thing the two branches of
   * the original createTicket/createTask pairs actually varied.
   */
  private withContactApp(email, currentUser, appName: string, action: (contact) => void): void {
    const contactInfo: Observable<any> = this.isSelf(email, currentUser)
      ? this.taskService.getLoggedInUserContactInfo(email)
      : this.taskService.getContactInfo(email);

    contactInfo.pipe(take(1)).subscribe(res => {
      const hasApp = res.products?.some(app => app.name.toLowerCase() === appName);
      if (hasApp) {
        action(res);
      } else {
        this.showMessage("NO_ACCESS_FOR_APP");
      }
    });
  }

  private isSelf(email, currentUser): boolean {
    return email === currentUser?.email;
  }

  private showMessage(key: string): void {
    const message = this.messageTranslatorService.getMessage(key);
    this.vncLibaryService.openSnackBar(message, "", "", "", 3000, "bottom", "left").pipe(take(1)).subscribe();
  }
}
